import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { SectionCard } from "#src/commonV2/SectionCard";
import { TableV2 } from "#src/commonV2/Table";
import { ISensitivityChangeRequest } from "#src/services/endpoint/dlp/types";
import { Dropdown, Tooltip, Typography } from "@lockerpm/design";
import {
  Tag01,
  DotsVertical,
  CheckCircle,
  XCircle,
} from "@untitled-ui/icons-react";
import { DateTime } from "luxon";
import { SensitivityEnum, SensitivityLabel } from "../Sensitivity";
import { useTranslation } from "react-i18next";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";
import { ReactNode, useMemo, useState } from "react";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import dlpService from "#src/services/endpoint/dlp";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { getIconFromFileExtension } from "../utils";

enum SensitivityRequestStatusEnum {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  CANCELLED = "auto_cancelled",
}

interface IEndpointDlpDataLabelSensitivityChangeSectionProps {
  workspaceId: string;
}

export const EndpointDlpDataLabelSensitivityChangeSection = ({
  workspaceId,
}: IEndpointDlpDataLabelSensitivityChangeSectionProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "dlp.dataLabeling.sensitivityChange",
  });

  // --------------- STATES ---------------

  const [sortOrder, setSortOrder] = useState<string>("");

  // --------------- HOOKS ---------------

  const fetchParams = useMemo<
    Parameters<typeof dlpService.listSensitivityChangeRequest>
  >(
    () => [
      workspaceId,
      { status: undefined, sort: sortOrder ? sortOrder : undefined },
    ],
    [workspaceId, sortOrder]
  );

  const {
    list: requestList,
    isLoading,
    updateData: updateRequestList,
  } = useFetchNoPaging(dlpService.listSensitivityChangeRequest, fetchParams);

  // --------------- METHODS ---------------

  const SensitivityChangeRequestStatus = ({ status }: { status: string }) => {
    const cardCommonTw =
      "py-0.5 px-2 flex items-center justify-center rounded-full w-full text-sm font-medium border";

    if (status === SensitivityRequestStatusEnum.REJECTED)
      return (
        <div
          className={`${cardCommonTw} text-error-v2-600 bg-error-v2-50 border-error-v2-200`}
        >
          {t("status.rejected")}
        </div>
      );

    if (status === SensitivityRequestStatusEnum.APPROVED)
      return (
        <div
          className={`${cardCommonTw} text-success-v2-600 bg-success-v2-50 border-success-v2-200`}
        >
          {t("status.approved")}
        </div>
      );

    if (status === SensitivityRequestStatusEnum.CANCELLED)
      return (
        <div
          className={`${cardCommonTw} text-gray-blue-v2-700 bg-gray-blue-v2-50 border-gray-blue-v2-200`}
        >
          {t("status.cancelled")}
        </div>
      );

    return (
      <div
        className={`${cardCommonTw} text-gray-v2-700 bg-gray-v2-50 border-gray-v2-200`}
      >
        {status}
      </div>
    );
  };

  const acceptSensitivityChangeRequest = (requestId: string) => {
    dlpService
      .updateSensitivityChangeRequest(
        workspaceId,
        requestId,
        SensitivityRequestStatusEnum.APPROVED
      )
      .then(() => {
        updateRequestList();
      })
      .catch(apiErrorHandler);
  };

  const rejectSensitivityChangeRequest = (requestId: string) => {
    dlpService
      .updateSensitivityChangeRequest(
        workspaceId,
        requestId,
        SensitivityRequestStatusEnum.REJECTED
      )
      .then(() => {
        updateRequestList();
      })
      .catch(apiErrorHandler);
  };

  const onClickActionItems = (key: string, item: ISensitivityChangeRequest) => {
    switch (key) {
      case "approve":
        acceptSensitivityChangeRequest(item.id);
        return;
      case "reject":
        rejectSensitivityChangeRequest(item.id);
        return;
      default:
        console.log("wrong action");
        return;
    }
  };

  // --------------- COMPUTED ---------------

  // TODO: should put action key into enum.
  const actionList: { key: string; label: ReactNode }[] = [
    {
      key: "approve",
      label: (
        <TableActionLabelV2
          icon={<CheckCircle className="h-5 w-5 text-success-v2-700" />}
          text={t("action.accept")}
        />
      ),
    },
    {
      key: "reject",
      label: (
        <TableActionLabelV2
          icon={<XCircle className="h-5 w-5 text-error-v2-700" />}
          text={t("action.reject")}
        />
      ),
    },
  ];

  // --------------- EFFECTS ---------------

  // --------------- RENDER ---------------

  return (
    <SectionCard
      header={
        <div className="flex items-center gap-2">
          <Tag01 className="h-5 w-5 min-w-5 text-gray-v2" />
          {t("title")}
        </div>
      }
    >
      <TableV2<ISensitivityChangeRequest>
        data={requestList ?? []}
        getRowKey={(item) => item.id}
        isLoading={isLoading}
        columns={[
          {
            title: t("headers.requestor"),
            render: (item) => (
              <Tooltip
                title={item.requester.email}
                className="font-medium text-gray-v2-900"
              >
                {item.requester.fullname}
              </Tooltip>
            ),
            gridTemplateValue: "1fr",
            sortingKey: { asc: "reporter_asc", desc: "reporter_desc" },
          },
          {
            title: t("headers.file"),
            render: (item) => (
              <Tooltip title={item.filename}>
                <div className="flex items-center gap-3 font-medium text-gray-v2-900">
                  {getIconFromFileExtension(item.filename, 2)}
                  {item.filename}
                </div>
              </Tooltip>
            ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("headers.currentTag"),
            render: (item) =>
              item.current_sensitivity.id ===
              SensitivityEnum.NOT_APPLICABLE.toString() ? (
                "---"
              ) : (
                <SensitivityLabel sensitivity={item.current_sensitivity} />
              ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("headers.updatedTag"),
            render: (item) =>
              item.new_sensitivity.id ===
              SensitivityEnum.NOT_APPLICABLE.toString() ? (
                "---"
              ) : (
                <SensitivityLabel sensitivity={item.new_sensitivity} />
              ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("headers.requestedTime"),
            render: (item) =>
              DateTime.fromSeconds(item.created_time).toFormat(
                "hh:mm dd/MM/yyyy"
              ),
            gridTemplateValue: "1fr",
            sortingKey: { asc: "created_time_asc", desc: "created_time_desc" },
          },
          {
            title: t("headers.reasoning"),
            render: (item) => (
              <Typography.Text
                ellipsis={{
                  tooltip: {
                    title: (
                      <p className="text-white text-xs font-semibold text-center">
                        {item.explanation}
                      </p>
                    ),
                    className: "text-gray-v2-900",
                  },
                }}
              >
                {item.explanation}
              </Typography.Text>
            ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("headers.status"),
            render: (item) => {
              if (item.status === SensitivityRequestStatusEnum.PENDING)
                return (
                  <Dropdown
                    menu={{
                      items: actionList.map((actionItem) => ({
                        key: actionItem.key,
                        label: actionItem.label,
                      })),
                      onClick: ({ key, domEvent }) => {
                        domEvent.preventDefault();
                        domEvent.stopPropagation();
                        onClickActionItems(key, item);
                      },
                    }}
                    dropdownRender={(menu) => (
                      <DropdownRenderV2>{menu}</DropdownRenderV2>
                    )}
                    trigger={["click"]}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <DotsVertical className="h-5 w-5 text-gray-v2-400 transition-colors duration-300" />
                    </button>
                  </Dropdown>
                );
              return <SensitivityChangeRequestStatus status={item.status} />;
            },
            gridTemplateValue: "8rem",
            overwriteWrapperProps: { className: "justify-end" },
          },
        ]}
        sort={{
          value: sortOrder,
          onChange: (value) => {
            setSortOrder(value);
          },
        }}
      />
    </SectionCard>
  );
};
